// Generated types for Requirement
// This file is auto-generated - do not edit manually

import { z } from 'zod';

import { Criticality, EarsPattern, MoscowPriority, RequirementCategory, RequirementLifecycle, VerificationMethod, VerificationStatus } from '../enums';

import { RFLPElementReadAPI, RFLPElementWriteAPI } from './rflpelement';
import { RFLPElementReadAPISchemaObject, RFLPElementWriteAPISchemaObject } from './rflpelement';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface RequirementReadAPI extends RFLPElementReadAPI {
  /** The requirement statement (natural language). */
  text: string;
  /** Why this requirement needs to exist — the constraint, the safety case, the stakeholder requirement. */
  rationale?: string | null;
  /** INCOSE category of what the STATEMENT is (its semantics alone); it sets the satisfaction evidence expected — never choose it to change that. functional and performance are both behaviour-backed; a mission system with ZERO functional requirements is miscategorised. */
  category?: RequirementCategory;
  /** MoSCoW priority. */
  priority?: MoscowPriority;
  /** Lifecycle status (approval axis). */
  status?: RequirementLifecycle;
  /** Free-form gate/release label (e.g. 'G2', 'v1-GA'). */
  target_milestone?: string | null;
  /** How we'll know this requirement is met. */
  acceptance_criteria?: string | null;
  /** Human-readable identifier (e.g. 'REQ-DRONE-0042'). Auto-allocated per Project on first flush; not writable by agents. */
  ref_id?: string;
  /** INCOSE attribute: how this requirement will be verified. Multi-select — any combination of TEST / ANALYSIS / INSPECTION / DEMONSTRATION (a requirement can be verified by more than one method). */
  verification_method?: VerificationMethod[];
  /** Verification axis — orthogonal to `status` (lifecycle). */
  verification_status?: VerificationStatus;
  /** Consequence of failure — DO-178 / MIL-STD aligned. Orthogonal to `priority` (MoSCoW stakeholder preference). */
  criticality?: Criticality;
  /** Free-form notes on risk drivers / mitigation context. */
  risk_notes?: string | null;
  /** EARS pattern (Ubiquitous / Event / State / Optional / Unwanted / Complex). Drives which structural fields apply. */
  ears_pattern?: EarsPattern | null;
  /** EARS event trigger ('When X...'). Event-driven and Complex patterns. */
  trigger?: string | null;
  /** EARS state precondition ('While Y...'). State-driven and Complex patterns. */
  state?: string | null;
  /** EARS unwanted-condition precondition ('If Z...'). Unwanted and Complex patterns. */
  precondition?: string | null;
  /** EARS response clause ('the system shall ...'). Required for all patterns. */
  response?: string | null;
  /** Most recent INCOSE conformance score (0..1). Written by the rule-check pipeline; None before first check. */
  incose_score?: number | null;
  /** INCOSE rule IDs (e.g. 'R7', 'R32') currently violating. */
  incose_violations?: string[];
  /** Timestamp of the last INCOSE rule check. */
  last_checked_at?: string | null;
  /** A USER decision that the numbers cannot be formalised as a Constraint (e.g. '3 attempts'; say why in rationale). Only on user instruction or for genuinely non-formalisable numbers — never to silence a completeness warning: report one you won't address, open. */
  formalisation_waived?: boolean;
}

export interface RequirementWriteAPI extends RFLPElementWriteAPI {
  /** The requirement statement (natural language). */
  text: string;
  /** Why this requirement needs to exist — the constraint, the safety case, the stakeholder requirement. */
  rationale?: string | null;
  /** INCOSE category of what the STATEMENT is (its semantics alone); it sets the satisfaction evidence expected — never choose it to change that. functional and performance are both behaviour-backed; a mission system with ZERO functional requirements is miscategorised. */
  category?: RequirementCategory;
  /** MoSCoW priority. */
  priority?: MoscowPriority;
  /** Lifecycle status (approval axis). */
  status?: RequirementLifecycle;
  /** Free-form gate/release label (e.g. 'G2', 'v1-GA'). */
  target_milestone?: string | null;
  /** How we'll know this requirement is met. */
  acceptance_criteria?: string | null;
  /** INCOSE attribute: how this requirement will be verified. Multi-select — any combination of TEST / ANALYSIS / INSPECTION / DEMONSTRATION (a requirement can be verified by more than one method). */
  verification_method?: VerificationMethod[];
  /** Verification axis — orthogonal to `status` (lifecycle). */
  verification_status?: VerificationStatus;
  /** Consequence of failure — DO-178 / MIL-STD aligned. Orthogonal to `priority` (MoSCoW stakeholder preference). */
  criticality?: Criticality;
  /** Free-form notes on risk drivers / mitigation context. */
  risk_notes?: string | null;
  /** EARS pattern (Ubiquitous / Event / State / Optional / Unwanted / Complex). Drives which structural fields apply. */
  ears_pattern?: EarsPattern | null;
  /** EARS event trigger ('When X...'). Event-driven and Complex patterns. */
  trigger?: string | null;
  /** EARS state precondition ('While Y...'). State-driven and Complex patterns. */
  state?: string | null;
  /** EARS unwanted-condition precondition ('If Z...'). Unwanted and Complex patterns. */
  precondition?: string | null;
  /** EARS response clause ('the system shall ...'). Required for all patterns. */
  response?: string | null;
  /** The element that contains this Requirement. */
  container?: string;
  sub_requirements?: (string | RequirementWriteAPI)[];
  refines?: string[];
  /** Requirements this one was derived FROM. Asymmetric — pairs with `derives_to`. This is flowdown PROVENANCE: remove an edge only when the derivation was genuinely wrong (re-scoping), never to stop a completeness rule (budget split, flowdown) from firing — stripping provenance orphans the requirement and is rejected by the commit invariants anyway. */
  derives_from?: string[];
  /** Requirements this one conflicts with. Semantically symmetric — when querying conflicts, walk both `conflicts_with` and `conflicts_with_in` to get the full set. */
  conflicts_with?: string[];
  /** INCOSE source / origin references — documents, standards, or captured stakeholder needs this Requirement derives from. */
  sources?: string[];
  /** StakeholderNeed(s) this Requirement was transformed from. Audit trail of NRS → RS transformation. */
  transformed_from?: string[];
  /** Concerns this Requirement frames (SysML v2 §7.21.3). */
  frames_concerns?: string[];
  /** A USER decision that the numbers cannot be formalised as a Constraint (e.g. '3 attempts'; say why in rationale). Only on user instruction or for genuinely non-formalisable numbers — never to silence a completeness warning: report one you won't address, open. */
  formalisation_waived?: boolean;
}

// ============================================================================
// Zod Schemas
// ============================================================================

export const RequirementReadAPISchemaObject = RFLPElementReadAPISchemaObject.extend({
  text: z.string(),
  rationale: z.string().nullable().optional(),
  category: z.nativeEnum(RequirementCategory).optional(),
  priority: z.nativeEnum(MoscowPriority).optional(),
  status: z.nativeEnum(RequirementLifecycle).optional(),
  target_milestone: z.string().nullable().optional(),
  acceptance_criteria: z.string().nullable().optional(),
  ref_id: z.string().optional(),
  verification_method: z.array(z.nativeEnum(VerificationMethod)).optional(),
  verification_status: z.nativeEnum(VerificationStatus).optional(),
  criticality: z.nativeEnum(Criticality).optional(),
  risk_notes: z.string().nullable().optional(),
  ears_pattern: z.nativeEnum(EarsPattern).nullable().optional(),
  trigger: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  precondition: z.string().nullable().optional(),
  response: z.string().nullable().optional(),
  incose_score: z.number().nullable().optional(),
  incose_violations: z.array(z.string()).optional(),
  last_checked_at: z.string().nullable().optional(),
  formalisation_waived: z.boolean().optional(),
}).passthrough();
export const RequirementReadAPISchema: z.ZodType<RequirementReadAPI> = RequirementReadAPISchemaObject;

export const RequirementWriteAPISchemaObject = RFLPElementWriteAPISchemaObject.extend({
  text: z.string(),
  rationale: z.string().nullable().optional(),
  category: z.nativeEnum(RequirementCategory).optional(),
  priority: z.nativeEnum(MoscowPriority).optional(),
  status: z.nativeEnum(RequirementLifecycle).optional(),
  target_milestone: z.string().nullable().optional(),
  acceptance_criteria: z.string().nullable().optional(),
  verification_method: z.array(z.nativeEnum(VerificationMethod)).optional(),
  verification_status: z.nativeEnum(VerificationStatus).optional(),
  criticality: z.nativeEnum(Criticality).optional(),
  risk_notes: z.string().nullable().optional(),
  ears_pattern: z.nativeEnum(EarsPattern).nullable().optional(),
  trigger: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  precondition: z.string().nullable().optional(),
  response: z.string().nullable().optional(),
  container: z.string().optional(),
  sub_requirements: z.array(z.union([z.string(), z.lazy(() => RequirementWriteAPISchema)])).optional(),
  refines: z.array(z.string()).optional(),
  derives_from: z.array(z.string()).optional(),
  conflicts_with: z.array(z.string()).optional(),
  sources: z.array(z.string()).optional(),
  transformed_from: z.array(z.string()).optional(),
  frames_concerns: z.array(z.string()).optional(),
  formalisation_waived: z.boolean().optional(),
}).passthrough();
export const RequirementWriteAPISchema: z.ZodType<RequirementWriteAPI> = RequirementWriteAPISchemaObject;
